import React, { useEffect, useState } from "react";
import { Button, Col, FormControl, InputGroup, Row, Spinner } from "react-bootstrap";
import { FaAddressBook } from "react-icons/fa";
import PrivateApi from "../../../services/PrivateApiService";
import { useQuery, useQueryClient } from "react-query";
import { useAuthDispatch, useAuthState } from "../../../context/AuthContext";
import DataTable from "react-data-table-component";
import EnergoTooltip from "../../shared/EnergoTooltip";

/**
 * 
 * @param {props} setStage setter for changing stage of modal
 * @param {props} setShow setter for displaying modal
 *   
 * @returns stage
 */
const EmployeeStage = ({setStage, setShow}) => {

    const API = new PrivateApi();
    const { user, contactPerson, tooltipsTrigger, tooltips } = useAuthState();
    const dispatch = useAuthDispatch(); // use auth context to dispatch commands
    const queryClient = useQueryClient();

    const [selectedUser, setSelectedUser] = useState(0);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchField, setSearchField] = useState("");
    const userColumns = [
        { name: "Name", selector: row => row.name, sortable: true},
        { name: "E-Mail-Adresse", selector: row => row.email, sortable: false},
    ]
    const paginationOptions = {
        rowsPerPageText: 'Zeilen pro Seite',
        rangeSeparatorText: 'von',
        selectAllRowsItemText: 'Alle'
    }
    
    const [newUserRow, setNewUserRow] = useState(false);
    const [newUserName, setNewUserName] = useState("");
    const [newUserEmail, setNewUserEmail] = useState("");
    const [newUserPhone, setNewUserPhone] = useState("");
    const [fieldErrors, setFieldsError] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [newUserSaveLoading, setNewUserSaveLoading] = useState(false);

    const { data, error, isLoading } = useQuery("GET_EMPLOYEES", API.get_Partner_employees, { staleTime: Infinity });

    useEffect(() => {
        if (data !== undefined && data.data.length > 0) {
            setFilteredUsers(data.data);
        }
        if (contactPerson !== undefined && contactPerson !== null && contactPerson.id !== undefined) {
            selectUser(contactPerson);
        }
    }, [data])


    // effect when search field changes
    useEffect(() => {
        if (data !== undefined && searchField !== "") {
            return setFilteredUsers(data.data.filter(u => 
                u.name.toLowerCase().includes(searchField.toLowerCase()) ||
                u.email.toLowerCase().includes(searchField.toLowerCase())
            ));
        }
        if (data !== undefined) {
            setFilteredUsers(data.data);
        }
    }, [searchField])


    // save new user
    const saveNewUser = () => {
        setErrorMessage("");
        setNewUserSaveLoading(true);
        if (newUserName.length < 4) return setFieldsError({newUserName : true});
        if (newUserEmail.length < 4 || newUserEmail.includes('@')) return setFieldsError({newUserEmail : true});
        if (newUserEmail.includes(user.email.split('@')[1])) return setFieldsError({newUserEmail: true});

        var fullMail = `${newUserEmail}@${user.email.split('@')[1]}`;
        var data = {name: newUserName, email: fullMail, phone: newUserPhone}
        API.post_Partner_employee_save(data)
            .then(res => {
                queryClient.invalidateQueries('GET_EMPLOYEES');
                setNewUserSaveLoading(false);
                setNewUserRow(false);
                // reset fields
                setNewUserName("");
                setNewUserEmail("");
                setNewUserPhone("");
                dispatch('POPULATE_CONTACT', res.data);
                setStage('default');
            })
            .catch(err => {
                if (err.response.data.detail !== undefined) {
                    setErrorMessage(err.response.data.detail);
                } else {
                    setErrorMessage("Unbekannter Fehler.");
                }
            })
    }

    // datatable handler to select user
    const selectUser = (row) => {
        const rows = document.querySelectorAll('[id*="row"]');
        const selectedRows = Array.from(rows).filter(elem => elem.classList.contains('bg-energo'));

        if (selectedRows.length > 0) {
            selectedRows.forEach(elem => {
                elem.classList.remove('bg-energo');
                setSelectedUser(0);
                dispatch('CLEAR_CONTACT');
            });
        }

        var index = selectedRows.findIndex(elem => elem.id === `row-${row.id}`);
        if (index === -1) {
            var elem = document.getElementById(`row-${row.id}`);
            if (elem) {
                elem.classList.add('bg-energo');
                setSelectedUser(row.id);
                dispatch('POPULATE_CONTACT', row);
            }
        }
    }

    return (
        <>
            {isLoading ?
                <Row className="mt-5 text-center">
                    <p>{user.name} Benutzer werden gesammelt...</p>
                    <Col xs="12">
                        <Spinner className="text-energo" animation="grow" /> &nbsp;
                        <Spinner className="text-energo" animation="grow" /> &nbsp;
                        <Spinner className="text-energo" animation="grow" />
                    </Col>
                </Row>
                :
                <>
                    {newUserRow?
                        <div style={{padding: 20, borderRadius: 15}}> 
                            <p className="text-energo">Neuer Kontakt</p>
                            <Row>
                                <Col xl="8">
                                    <FormControl 
                                        placeholder="Name und Nachname *" 
                                        value={newUserName}
                                        onChange={e => {setNewUserName(e.target.value); setFieldsError({})}}
                                        isValid={newUserName.length > 3}
                                        isInvalid={fieldErrors.newUserName}
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col xl="8">
                                    <InputGroup>
                                        <FormControl
                                            placeholder="E-Mail-Adresse *"
                                            value={newUserEmail}
                                            onChange={e => {setNewUserEmail(e.target.value); setFieldsError({})}}
                                            isValid={newUserEmail.length > 3 && !newUserEmail.includes('@')}
                                            isInvalid={fieldErrors.newUserEmail}
                                        />
                                        <InputGroup.Text>@{user.email.split('@')[1]}</InputGroup.Text>
                                    </InputGroup>
                                </Col>
                                <Col xl="4">
                                    <FormControl 
                                        placeholder="Telefonnummer" 
                                        value={newUserPhone}
                                        onChange={e => setNewUserPhone(e.target.value)} 
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col xl="12" className="text-end">
                                    <Button onClick={() => setNewUserRow(false)} style={{marginRight: "10px"}} variant="warning">Abbrechen</Button>
                                    {newUserSaveLoading?
                                        <Spinner animation="border" className="text-energo" /> :
                                        <Button onClick={() => saveNewUser()} variant="primary">Speichern</Button>
                                    }
                                </Col>
                            </Row>
                        </div>
                    :
                        <>
                            <Row className="justify-content-center align-item-center">
                                <div style={{display: "flex"}}>
                                    <p style={{marginRight: "10px"}} className="text-energo">Bitte wählen Sie Ihre Kontaktinformation aus</p>
                                    <EnergoTooltip tKey="101" position="right" />
                                </div>
                                <Col xl="8">
                                    <FormControl
                                        type="search"
                                        placeholder="Name, Nachname, E-Mail-Adresse"
                                        value={searchField}
                                        onChange={e => setSearchField(e.target.value)}
                                    />
                                </Col>
                                <Col xl="2">
                                    <Button onClick={() => setNewUserRow(!newUserRow)} className="btn-energo"><FaAddressBook size={25} /> Neuer Kontakt</Button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col xl="12">
                                    <DataTable
                                        data={filteredUsers}
                                        columns={userColumns}
                                        noDataComponent="Keine Kontaktinformation gefunden"
                                        onRowClicked={row => selectUser(row)}
                                        paginationComponentOptions={paginationOptions}
                                        pagination
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-5">
                                <Col xl="12" className="text-end">
                                    <Button
                                        variant="warning"
                                        style={{marginRight: 10}}
                                        onClick={() => setShow(false)}
                                    >Abbrechen</Button>
                                    <Button
                                        hidden={selectedUser === 0}
                                        className="btn-energo"
                                        onClick={() => setStage('default')}
                                    >
                                        Weiter
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    }
                </>
            }
        </>
    )
}
export default EmployeeStage;